<template lang="pug">
UiModal(
  centered
  :show="show"
  @close="close()"
  :title="$t('companies.storages.sftp.title')"
  :subtitle="isCreate ? $t('companies.storages.sftp.create') : $t('companies.storages.sftp.update')"
  :rightActions="rightActions"
  width="560"
)
  UiFormGroup(
    :label="$t('companies.storages.sftp.name')"
    :vuelidateModel="v$.form.name"
  )
    UiInput(
      v-model="form.name"
      :error="v$.form.name.$error"
      :disabled="inProgress"
    )
  UiFormGroup
    UiStack
      UiStackItem(fill)
        UiFormGroup(
          :label="$t('companies.storages.sftp.host')"
          :vuelidateModel="v$.form.host"
        )
          UiInput#sftp-host-input(
            v-model="form.host"
            :error="v$.form.host.$error"
            :disabled="inProgress"
          )
      UiStackItem
        UiFormGroup(
          :label="$t('companies.storages.sftp.port')"
          :vuelidateModel="v$.form.port"
        )
          UiInput#sftp-port-input(
            v-model="form.port"
            placeholder="22"
            inputSize="10"
            :error="v$.form.port.$error"
            :disabled="inProgress"
          )
  UiFormGroup(
    :label="$t('companies.storages.sftp.username')"
    :vuelidateModel="v$.form.username"
  )
    UiInput#sftp-username-input(
      v-model="form.username"
      :error="v$.form.username.$error"
      :disabled="inProgress"
    )
  UiFormGroup(
    :label="$t('companies.storages.sftp.password')"
    :vuelidateModel="v$.password"
  )
    UiInput#sftp-password-input(
      v-model="form.password"
      :error="v$.form.password.$error"
      :disabled="inProgress"
    )
  UiFormGroup
    UiCheckbox#default-input(
      v-model="form.default"
      switch
    ) {{ $t(`companies.storages.sftp.default`) }}
  UiFormGroup(v-if="sftpError")
    UiAlert(
      variant="error"
      :closable="false"
      title="SFTP connection error:"
    ) {{ sftpError }}
  UiFormGroup
    UiStack(
      vertical
      spacing="sm"
      v-if="!isCreate"
    )
      UiFormGroup(:label="$t('companies.storages.sftp.path')")
        UiStack(spacing="xs")
          UiStackItem
            UiButton(
              :disabled="form.remote_path === './'"
              variant="primary"
              @click="SFTPFolderUp"
              icon="FolderPlus"
              round
            )
          UiStackItem(fill)
            UiInput(
              v-model="form.remote_path"
              readonly
            )
          UiStackItem
            UiButton(
              variant="smooth-primary"
              @click="save"
              icon="FolderOpened"
            ) {{ $t("companies.storages.sftp.connect") }}
      UiMultiselect(
        v-model="sftpSelectedFolder"
        :disabled="!sftpFolders.length || inProgress"
        :options="sftpFolders"
        :select-size="sftpFolders.length + 1"
      )
</template>

<script>
import ApiCompanyStorages from "@/api/companyStorages.js";
import useVuelidate from "@vuelidate/core";
import { required, requiredIf } from "@vuelidate/validators";

export default {
  setup() {
    return {
      v$: useVuelidate(),
    };
  },

  validations() {
    return {
      form: {
        name: {
          required,
        },
        host: {
          required,
        },
        port: {
          required,
        },
        username: {
          required,
        },
        password: {
          required: requiredIf(this.isCreate),
        },
      },
    };
  },

  data() {
    return {
      show: false,
      isCreate: false,
      companyId: null,
      form: {
        name: "",
        path: "",
        host: "",
        port: "",
        username: "",
        password: "",
        remote_path: null,
      },
      sftpFolders: [],
      sftpSelectedFolder: null,
      sftpError: null,
      inProgress: false,
    };
  },

  watch: {
    sftpSelectedFolder: function (value) {
      if (value) {
        this.changeSelectedSFTPFolder(value);
      }
    },
  },

  computed: {
    rightActions() {
      return [
        {
          label: this.$t("actions.save"),
          onClick: () => this.save(true),
          icon: "Folder",
        },
      ];
    },
  },

  methods: {
    open(companyId, sftp) {
      if (sftp) {
        this.isCreate = false;
        this.form = {
          id: sftp.id,
          name: sftp.name,
          default: sftp.default,
          host: sftp.host,
          port: sftp.port,
          remote_path: sftp.remote_path,
          username: sftp.username,
        };
        this.form.remote_path = sftp.remote_path;
      } else {
        this.isCreate = true;
        this.companyId = null;
        this.form = {};
        this.sftpSelectedFolder = null;
        this.form.remote_path = null;
      }
      this.sftpFolders = [];
      this.sftpError = null;
      this.companyId = companyId;
      this.show = true;
    },

    close() {
      this.show = false;
    },

    save(closeAfterSave = true) {
      this.v$.$validate().then((res) => {
        if (!res) return;
        this.inProgress = true;
        if (this.isCreate) {
          this.createSFTP(closeAfterSave).then(() => {
            this.SFTPFolderUp();
          });
        } else {
          return this.updateSFTP(closeAfterSave);
        }
      });
    },

    updateSFTP(closeAfterSave) {
      let storageData = {
        storage: this.form,
      };
      this.v$.$validate().then((res) => {
        return ApiCompanyStorages.updateSFTP(
          this.$route.params.id,
          this.form.id,
          storageData,
        ).then((resp) => {
          if (resp.data.sftp) {
            this.getData(resp.data.sftp.id);
            this.form.id = resp.data.sftp.id;
            if (closeAfterSave == true) {
              this.close();
              this.$emit("done");
            }
          }
          this.sftpError = resp.data.error;
          this.inProgress = false;
        });
      });
    },
    createSFTP(closeAfterSave) {
      let storageData = {
        storage: this.form,
      };
      this.v$.$validate().then((res) => {
        return ApiCompanyStorages.createSFTP(
          this.$route.params.id,
          storageData,
        ).then((resp) => {
          if (resp.data.sftp) {
            this.getData(resp.data.sftp.id);
            this.form.id = resp.data.sftp.id;
            if (closeAfterSave == true) {
              this.close();
              this.$emit("done");
            }
          }
          this.sftpError = resp.data.error;
          this.inProgress = false;
        });
      });
    },
    SFTPFolderUp() {
      if (this.form.id == undefined) {
        this.sftpError = "Create SFTP connection first";
        return;
      }
      if (this.form.remote_path == "./") {
        return;
      }
      this.form.remote_path = this.form.remote_path
        .split("/")
        .slice(0, -1)
        .join("/");
      if (this.form.remote_path === ".") {
        this.form.remote_path = "./";
      }
      this.inProgress = true;
      ApiCompanyStorages.SFTPFolderList(
        this.$route.params.id,
        this.form.remote_path,
        this.form.id,
      ).then((resp) => {
        this.sftpFolders = resp.data.folders;
        this.getData(this.form.id);
        this.inProgress = false;
      });
    },

    changeSelectedSFTPFolder(folder) {
      this.inProgress = true;
      let path =
        this.form.remote_path === "./"
          ? `${this.form.remote_path}${this.sftpSelectedFolder}`
          : `${this.form.remote_path}/${this.sftpSelectedFolder}`;
      ApiCompanyStorages.SFTPFolderList(
        this.$route.params.id,
        path,
        this.form.id,
      ).then((resp) => {
        this.sftpFolders = resp.data.folders;
        this.form.remote_path = path;
        this.inProgress = false;
        this.sftpSelectedFolder = null;
      });
    },

    getData(sftp_id) {
      ApiCompanyStorages.SFTPFolderList(
        this.$route.params.id,
        this.form.remote_path,
        sftp_id,
      ).then((resp) => {
        this.sftpFolders = resp.data.folders;
        this.sftpError = resp.data.error;
      });
    },
  },
};
</script>
